import { deleteCookie } from '@intigral/utils';
import { SCREEN_NAME_MAPPING } from '../config/constants';
import {
    getGuestRedirectionUrl,
    guestPrimaryRouteSegment,
    isGuestUser,
    isSignedInSessionCookie,
    readSessionCookie,
} from '../config/guestMode';
import h from '../helpers/common';
import user from '../helpers/user';
import { getUserInfo } from './user';
import { isGuestBrowseAllowed } from '../guest/exploreIntent';

const DEST_KEY = 'wcpPendingDeepLink';
const NEXT_KEY = 'wcpPostProfileOpenUrl';
const LOGIN_REDIRECT_KEY = 'redirect';

const SETUP_PAGES = new Set([
    'select-profile',
    'add-profile',
    'edit-profile',
    'cold-start',
    'consent-management',
    'parental-pin-management',
    'exit-kid',
]);

const KNOWN_PAGES = new Set([
    ...Object.keys(SCREEN_NAME_MAPPING),
    'newandhot',
    'providers',
    'rewards',
    'redeem',
    'discover',
    'kids',
]);

const HOME_QUERY_KEYS = ['eventId', 'type', 'movie', 'guid', 'seriesId', 'programId'];

let firstLoadDone = false;

function sessionGet(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function sessionSet(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        /* tests / private mode */
    }
}

function sessionClear(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        /* ignore */
    }
}

function pageName(pathname: string): string | undefined {
    return guestPrimaryRouteSegment(pathname)?.toLowerCase();
}

function relativeUrl(pathname: string, search = '', hash = ''): string {
    return `${pathname || '/'}${search || ''}${hash || ''}`;
}

function splitUrl(relative: string): { pathname: string; search: string } {
    try {
        const url = new URL(relative, 'http://wcp.local');
        return { pathname: url.pathname, search: url.search };
    } catch {
        return { pathname: relative, search: '' };
    }
}

function homeHasContentQuery(search: string): boolean {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    return HOME_QUERY_KEYS.some((key) => {
        const value = params.get(key);
        return key === 'type' ? value === 'Deeplink' : Boolean(value);
    });
}

function isCmsPage(page: string): boolean {
    try {
        const config = h.getAppConfig?.();
        if (config?.routes?.includes(page)) {
            return true;
        }
        return Boolean(config?.mlp?.some((item: { path?: string }) => item?.path === page));
    } catch {
        return false;
    }
}

function isSetupPage(pathname: string): boolean {
    const page = pageName(pathname);
    return Boolean(page && SETUP_PAGES.has(page));
}

function isHomePath(pathname: string): boolean {
    return !pageName(pathname);
}

function isReloadNavigation(): boolean {
    try {
        const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'reload') {
            return true;
        }
        return (performance as Performance & { navigation?: { type?: number } }).navigation?.type === 1;
    } catch {
        return false;
    }
}

function isAccountPath(pathname: string): boolean {
    const normalized = (pathname || '').replace(/\/+$/, '') || '/';
    return /(^|\/)account$/i.test(normalized);
}

function isReturnFromAccount(): boolean {
    try {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return false;
        }
        const ref = document.referrer;
        if (!ref) {
            return false;
        }
        const url = new URL(ref);
        if (url.origin !== window.location.origin) {
            return false;
        }
        return isAccountPath(url.pathname);
    } catch {
        return false;
    }
}

export function isUnsignedUser(): boolean {
    if (isSignedInSessionCookie(readSessionCookie())) {
        return false;
    }
    const local = getUserInfo();
    return !(local?.username && !isGuestUser(local));
}

export function isContentDeepLink(pathname: string, _search = ''): boolean {
    return !isSetupPage(pathname);
}

export function isKnownWcpRoute(relative: string): boolean {
    if (!relative) {
        return false;
    }
    const { pathname } = splitUrl(relative);
    if (isSetupPage(pathname)) {
        return false;
    }
    if (isHomePath(pathname)) {
        return true;
    }
    const page = pageName(pathname);
    return Boolean(page && (KNOWN_PAGES.has(page) || isCmsPage(page)));
}

function saveDest(pathname: string, search = '', hash = ''): string | null {
    if (!isContentDeepLink(pathname, search)) {
        return sessionGet(DEST_KEY);
    }
    const dest = relativeUrl(pathname, search, hash);
    const existing = sessionGet(DEST_KEY);
    // Login may land on WCP home; do not replace a screen dest with empty home.
    if (existing && isHomePath(pathname) && !homeHasContentQuery(search) && !isHomePath(splitUrl(existing).pathname)) {
        return existing;
    }
    sessionSet(DEST_KEY, dest);
    sessionClear(LOGIN_REDIRECT_KEY);
    try {
        deleteCookie(LOGIN_REDIRECT_KEY);
    } catch {
        /* no document in tests */
    }
    return dest;
}

export function resetDeepLinkFirstLoad(): void {
    firstLoadDone = false;
}

export function handleDeepLinkOnFirstLoad(
    pathname: string,
    search = '',
    hash = ''
): { dest: string | null; goToSelectProfile: boolean } {
    if (firstLoadDone) {
        return { dest: sessionGet(DEST_KEY), goToSelectProfile: false };
    }
    firstLoadDone = true;
    if (isReloadNavigation()) {
        return { dest: sessionGet(DEST_KEY), goToSelectProfile: false };
    }

    if (isReturnFromAccount() && isHomePath(pathname) && !homeHasContentQuery(search)) {
        return { dest: sessionGet(DEST_KEY), goToSelectProfile: false };
    }
    const dest = saveDest(pathname, search, hash);
    return {
        dest,
        goToSelectProfile: Boolean(dest) && pageName(pathname) !== 'select-profile' && !isUnsignedUser(),
    };
}

function redirectToLogin(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const login = getGuestRedirectionUrl(user.getUserLogoutUrl());
    if (!login) {
        return false;
    }
    try {
        const url = new URL(login, window.location.origin);
        url.searchParams.set('redirect', window.location.href);
        window.location.assign(url.toString());
    } catch {
        window.location.assign(login);
    }
    return true;
}

export function sendUnsignedUserToLoginIfDeepLink(): boolean {
    if (typeof window === 'undefined' || isReloadNavigation() || !isUnsignedUser() || isGuestBrowseAllowed()) {
        return false;
    }
    const { pathname, search, hash } = window.location;
    if (!isContentDeepLink(pathname, search)) {
        return false;
    }
    saveDest(pathname, search, hash);
    return redirectToLogin();
}

export function handleDeepLinkOnAppStart(args: {
    pathname: string;
    search: string;
    hash: string;
    onSelectProfile: () => void;
}): void {
    if (sendUnsignedUserToLoginIfDeepLink()) {
        return;
    }
    const { goToSelectProfile } = handleDeepLinkOnFirstLoad(args.pathname, args.search, args.hash);
    if (goToSelectProfile) {
        args.onSelectProfile();
    }
}

function needsSetupFirst(url: string): boolean {
    return /cold-start|parental-pin-management/i.test(url || '');
}

export function urlAfterProfileSelect(fallbackUrl: string): string {
    if (needsSetupFirst(fallbackUrl)) {
        return fallbackUrl;
    }
    const dest = sessionGet(DEST_KEY);
    sessionClear(DEST_KEY);
    if (dest && isKnownWcpRoute(dest)) {
        return dest;
    }
    return fallbackUrl;
}

export function queueUrlAfterProfileSelect(fallbackUrl: string): string {
    const url = urlAfterProfileSelect(fallbackUrl);
    if (url) {
        sessionSet(NEXT_KEY, url);
    }
    return url;
}

export function takeQueuedUrlAfterProfileSelect(): string | null {
    const url = sessionGet(NEXT_KEY);
    sessionClear(NEXT_KEY);
    return url;
}
